import React, { useEffect, useState } from 'react';
import { ChartLayout, EquityContent, MethodologyContent, OverviewContent, RelevanceContent } from './ChartLayout';
import { SectorBasedGHGInventory } from './SectorBasedGHGInventory';
import { KeyMetric, KeyMetrics } from './KeyMetrics';
import { fetchGHGInventoryData, GHGInventoryResult } from './GHGInventorySupabase';
import { bindDownloadHandler } from '../../utils/downloadUtils';
import {
  SECTOR_GHG_COMPREHENSIVE_DESCRIPTION,
  SECTOR_GHG_DATA_SOURCES,
  SECTOR_GHG_METHODOLOGY,
  SECTOR_GHG_COMPREHENSIVE_TITLE,
} from './ghgInventorySectorMethodology';
import { SectorGhgPerCapitaVsCaliforniaChart } from './SectorGhgPerCapitaVsCaliforniaChart';

type ChartType = 'stacked-area' | 'line' | 'pie' | 'per-capita';

interface SectorBasedGHGInventoryWithMetricsProps {
  location: string;
}

export const SectorBasedGHGInventoryWithMetrics: React.FC<SectorBasedGHGInventoryWithMetricsProps> = ({ location }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [chartType, setChartType] = useState<ChartType>('stacked-area');
  const [activeView, setActiveView] = useState<string>('overview');
  const [ghgData, setGhgData] = useState<GHGInventoryResult | null>(null);
  const [downloadPNG, setDownloadPNG] = useState<(() => void) | undefined>();
  const [downloadCSV, setDownloadCSV] = useState<(() => void) | undefined>();

  // Load GHG inventory immediately. VMT is preloaded in background by LocationContext when location is set;
  // fetchGHGInventoryData uses cached VMT if ready, otherwise fetches VMT directly (no blocking).
  useEffect(() => {
    if (!location) return;

    const loadGHGData = async () => {
      try {
        setLoading(true);
        setError(null);
        const result = await fetchGHGInventoryData(location);
        setGhgData(result);
      } catch (err) {
        console.error('Error loading GHG inventory data:', err);
        setError(err instanceof Error ? err.message : 'Failed to load GHG inventory data');
      } finally {
        setLoading(false);
      }
    };

    loadGHGData();
  }, [location]);

  if (loading) {
    return (
      <ChartLayout
        title="Sector-Based GHG Inventory"
        chartContent={
          <div style={{ padding: '40px', textAlign: 'center' }}>
            <div style={{ fontSize: '16px', color: '#6b7280' }}>
              Loading GHG inventory data...
            </div>
          </div>
        }
        description="Greenhouse gas emissions inventory organized by economic sectors and activities."
        chartType="line"
        keyMetrics={
          <KeyMetrics>
            <KeyMetric value="Loading..." label="Total emissions (2023)" color="#1e3a8a" />
            <KeyMetric value="Loading..." label="Total emissions (2005)" color="#334155" />
            <KeyMetric value="Loading..." label="Change since 2005 (2005→2023)" color="#64748b" />
            <KeyMetric value="Loading..." label="Largest sector share (2023)" color="#64748b" />
          </KeyMetrics>
        }
        equityContent={
          <EquityContent
            title="Equity Considerations"
            description="Sector-based inventories help identify which economic sectors contribute most to emissions and where interventions can be most effective."
          >
            <div className="methodology-details-simple">
              <h5>Economic Impact</h5>
              <p>Understanding sector contributions helps prioritize climate action in areas with highest emissions impact.</p>
            </div>
            <div className="methodology-details-simple">
              <h5>Policy Targeting</h5>
              <p>Sector-based data enables targeted policies for specific industries and activities.</p>
            </div>
          </EquityContent>
        }
        methodologyContent={
          <MethodologyContent
            title="Sector-Based GHG Inventory Methodology"
            description="This inventory follows standard protocols for tracking emissions by economic sector and activity type."
            dataSources={[
              'California Air Resources Board (CARB)',
              'Local utility data',
              'Transportation surveys',
              'Building energy data'
            ]}
            methodology={[
              '<strong>Data Collection:</strong> Gather emissions data from major sectors including transportation, energy, buildings, and waste',
              '<strong>Activity Data:</strong> Collect activity data such as vehicle miles traveled, energy consumption, and waste generation',
              '<strong>Emission Factors:</strong> Apply appropriate emission factors to convert activity data to CO₂ equivalent emissions',
              '<strong>Aggregation:</strong> Sum emissions by sector and year to create comprehensive inventory'
            ]}
          />
        }
        overviewContent={
          <OverviewContent
            title="Sector-Based GHG Inventory Overview"
            description="This inventory tracks greenhouse gas emissions organized by economic sectors and activities."
          >
            <p>The sector-based approach categorizes emissions by economic activity, making it easier to identify the largest sources and develop targeted reduction strategies.</p>
            <p>Key sectors typically include transportation, energy, buildings, waste, and other activities specific to the community.</p>
          </OverviewContent>
        }
        relevanceContent={
          <RelevanceContent
            title="Relevance to Climate Action"
            description="Sector-based inventories provide the foundation for developing comprehensive climate action plans."
          >
            <div className="methodology-details-simple">
              <h5>Policy Development</h5>
              <p>Sector-based data helps policymakers prioritize actions and allocate resources effectively.</p>
            </div>
            <div className="methodology-details-simple">
              <h5>Progress Tracking</h5>
              <p>Regular inventories enable communities to track progress toward emission reduction goals.</p>
            </div>
          </RelevanceContent>
        }
        activeView={activeView}
        onViewChange={setActiveView}
        showRelevanceEquity={true}
        location={location}
        appendLocationToTitle={true}
        overviewSectionId="ghg_inventory"
        overviewChartId="sector_based_ghg"
      />
    );
  }

  if (error) {
    return (
      <ChartLayout
        title="Sector-Based GHG Inventory"
        description="Greenhouse gas emissions inventory organized by economic sectors and activities."
        chartType="line"
        keyMetrics={[]}
        chartContent={<div style={{ textAlign: 'center', padding: '40px', color: '#ef4444' }}>Error loading sector-based GHG data: {error}</div>}
        equityContent={
          <EquityContent
            title="Equity Considerations"
            description="Sector-based inventories help identify which economic sectors contribute most to emissions and where interventions can be most effective."
          >
            <div className="methodology-details-simple">
              <h5>Economic Impact</h5>
              <p>Understanding sector contributions helps prioritize climate action in areas with highest emissions impact.</p>
            </div>
            <div className="methodology-details-simple">
              <h5>Policy Targeting</h5>
              <p>Sector-based data enables targeted policies for specific industries and activities.</p>
            </div>
          </EquityContent>
        }
        methodologyContent={
          <MethodologyContent
            title="Sector-Based GHG Inventory Methodology"
            description="This inventory follows standard protocols for tracking emissions by economic sector and activity type."
            dataSources={[
              'California Air Resources Board (CARB)',
              'Local utility data',
              'Transportation surveys',
              'Building energy data'
            ]}
            methodology={[
              '<strong>Data Collection:</strong> Gather emissions data from major sectors including transportation, energy, buildings, and waste',
              '<strong>Activity Data:</strong> Collect activity data such as vehicle miles traveled, energy consumption, and waste generation',
              '<strong>Emission Factors:</strong> Apply appropriate emission factors to convert activity data to CO₂ equivalent emissions',
              '<strong>Aggregation:</strong> Sum emissions by sector and year to create comprehensive inventory'
            ]}
          />
        }
        overviewContent={
          <OverviewContent
            title="Sector-Based GHG Inventory Overview"
            description="This inventory tracks greenhouse gas emissions organized by economic sectors and activities."
          >
            <p>The sector-based approach categorizes emissions by economic activity, making it easier to identify the largest sources and develop targeted reduction strategies.</p>
            <p>Key sectors typically include transportation, energy, buildings, waste, and other activities specific to the community.</p>
          </OverviewContent>
        }
        relevanceContent={
          <RelevanceContent
            title="Relevance to Climate Action"
            description="Sector-based inventories provide the foundation for developing comprehensive climate action plans."
          >
            <div className="methodology-details-simple">
              <h5>Policy Development</h5>
              <p>Sector-based data helps policymakers prioritize actions and allocate resources effectively.</p>
            </div>
            <div className="methodology-details-simple">
              <h5>Progress Tracking</h5>
              <p>Regular inventories enable communities to track progress toward emission reduction goals.</p>
            </div>
          </RelevanceContent>
        }
        activeView={activeView}
        onViewChange={setActiveView}
        showRelevanceEquity={true}
        location={location}
        appendLocationToTitle={true}
        overviewSectionId="ghg_inventory"
        overviewChartId="sector_based_ghg"
      />
    );
  }

  const formatMt = (mt: number | undefined) => {
    if (mt == null || !Number.isFinite(mt)) return '—';
    return `${Math.round(mt).toLocaleString()} MT CO₂e`;
  };

  const formatPctSince2005 = (pct: number | undefined) => {
    if (pct == null || !Number.isFinite(pct)) return '—';
    return `${pct >= 0 ? '+' : ''}${pct.toFixed(1)}%`;
  };

  const formatLargestShare = (pctShare: number | undefined) => {
    if (pctShare == null || !Number.isFinite(pctShare)) return '—';
    return `${pctShare.toFixed(1)}%`;
  };

  const pct = ghgData?.percentChange;
  const pctColor =
    pct == null || !Number.isFinite(pct) ? '#64748b' : pct < 0 ? '#059669' : pct > 0 ? '#dc2626' : '#64748b';

  const largestLabel =
    ghgData?.largestSectorName2023 != null
      ? `Largest sector (2023): ${ghgData.largestSectorName2023}`
      : 'Largest sector share (2023)';

  const wwYear = ghgData?.wastewaterMeasuredYear;
  const wwLabel = wwYear != null ? `Wastewater (${wwYear}, included in total)` : 'Wastewater';

  const keyMetrics = (
    <KeyMetrics>
      <KeyMetric value={formatMt(ghgData?.totalEmissions2023)} label="Total emissions (2023)" color="#1e3a8a" />
      <KeyMetric value={formatMt(ghgData?.totalEmissions2005)} label="Total emissions (2005)" color="#334155" />
      <KeyMetric value={formatPctSince2005(pct)} label="Change since 2005 (2005→2023)" color={pctColor} />
      <KeyMetric
        value={formatLargestShare(ghgData?.largestSectorSharePercent2023)}
        label={largestLabel}
        color="#0f766e"
      />
      {ghgData?.wastewaterEmissions2020 != null && (
        <KeyMetric
          value={formatMt(ghgData.wastewaterEmissions2020)}
          label={wwLabel}
          color="#0ea5e9"
        />
      )}
    </KeyMetrics>
  );

  return (
    <ChartLayout
      title="Sector-Based GHG Inventory"
      description="Greenhouse gas emissions inventory organized by economic sectors and activities."
      chartType="line"
      keyMetrics={keyMetrics}
      chartContent={
        chartType === 'per-capita' ? (
          <SectorGhgPerCapitaVsCaliforniaChart
            location={location}
            ghgData={ghgData}
            onDownloadPNGReady={bindDownloadHandler(setDownloadPNG)}
            onDownloadCSVReady={bindDownloadHandler(setDownloadCSV)}
          />
        ) : (
          <SectorBasedGHGInventory
            location={location}
            chartType={chartType}
            setChartType={setChartType}
            ghgData={ghgData}
            onDownloadPNGReady={bindDownloadHandler(setDownloadPNG)}
            onDownloadCSVReady={bindDownloadHandler(setDownloadCSV)}
          />
        )
      }
      chartSelector={{
        options: [
          { value: 'stacked-area', label: 'Stacked Area' },
          { value: 'line', label: 'Line Chart' },
          { value: 'pie', label: 'Pie Chart (2023)' },
          { value: 'per-capita', label: 'Per capita vs CA' },
        ],
        value: chartType,
        onChange: (value) => setChartType(value as ChartType)
      }}
      equityContent={
        <EquityContent
          title="Equity Considerations"
          description="GHG inventories must consider equity implications across different communities and demographic groups."
        >
          <div className="space-y-3">
            <p>
              <strong>Disproportionate Impacts:</strong> Some communities may face higher exposure to emissions sources and greater vulnerability to climate impacts.
            </p>
            <p>
              <strong>Access to Solutions:</strong> Climate solutions must be accessible to all income levels to ensure equitable participation in emissions reductions.
            </p>
            <p>
              <strong>Community Benefits:</strong> Climate investments should prioritize communities that have historically borne disproportionate environmental burdens.
            </p>
          </div>
        </EquityContent>
      }
      methodologyContent={
        <MethodologyContent
          title={SECTOR_GHG_COMPREHENSIVE_TITLE}
          description={SECTOR_GHG_COMPREHENSIVE_DESCRIPTION}
          dataSources={[...SECTOR_GHG_DATA_SOURCES]}
          methodology={[...SECTOR_GHG_METHODOLOGY]}
        />
      }
      overviewContent={
        <OverviewContent
          title="GHG Inventory Overview"
          description="Comprehensive greenhouse gas emissions tracking by economic sector."
        >
          <p>
            In 2023, {location} had total sector-based greenhouse gas emissions of{' '}
            {ghgData?.totalEmissions2023 != null
              ? `${Math.round(ghgData.totalEmissions2023).toLocaleString()}`
              : '—'}{' '}
            metric tons CO₂ equivalent
            {ghgData?.totalEmissions2005 != null ? (
              <>
                , compared to {Math.round(ghgData.totalEmissions2005).toLocaleString()} MT CO₂e in 2005
                {pct != null && Number.isFinite(pct) ? (
                  <> ({formatPctSince2005(pct)} change from 2005 to 2023)</>
                ) : null}
              </>
            ) : null}
            .
            {ghgData?.largestSectorName2023 != null &&
            ghgData?.largestSectorSharePercent2023 != null &&
            Number.isFinite(ghgData.largestSectorSharePercent2023) ? (
              <>
                {' '}
                The largest single category in 2023 is {ghgData.largestSectorName2023}, at{' '}
                {ghgData.largestSectorSharePercent2023.toFixed(1)}% of the 2023 total.
              </>
            ) : null}{' '}
            See Methodology section for data sources and calculations
          </p>
        </OverviewContent>
      }
      relevanceContent={
        <RelevanceContent
          title="Relevance to Climate Action"
          description="GHG inventory data provides the foundation for climate action planning and emissions reduction strategies."
        >
          <div className="space-y-3">
            <p>
              <strong>Baseline Establishment:</strong> GHG inventories establish baseline emissions levels needed for setting reduction targets and tracking progress.
            </p>
            <p>
              <strong>Sector Prioritization:</strong> Understanding which sectors contribute most to emissions helps prioritize climate investments and policy interventions.
            </p>
            <p>
              <strong>Progress Tracking:</strong> Regular inventory updates enable monitoring of emission trends and evaluation of climate action effectiveness.
            </p>
          </div>
        </RelevanceContent>
      }
      activeView={activeView}
      onViewChange={setActiveView}
      showRelevanceEquity={true}
      location={location}
      appendLocationToTitle={true}
      overviewSectionId="ghg_inventory"
      overviewChartId="sector_based_ghg"
      onDownloadPNG={downloadPNG}
      onDownloadCSV={downloadCSV}
    />
  );
};